import type { LocalPrivateSpecialistsConfig, SuggestionPlan } from './config';
import {
  doctorNameKey,
  hasIndexedInPersonTurn,
  meetsQualityFloor,
  rankLocalSpecialists,
  smoothedSatisfaction,
  uniqueByDoctorName,
} from './rankLocalSpecialists';

/**
 * Scenario mode (config.plan — Dr. Ebrahimi, Dr. Sakhanvar, Dr. Talebian). Four kinds of suggestion:
 * - `preferred`: the doctors patients booked themselves after this profile (named per profile);
 * - `local-online`: online visit with doctors based in the city;
 * - `office`: the regular in-person list (rankLocalSpecialists);
 * - `national-online`: the platform's best online doctors of the specialty, any city.
 * Display: preferred (local online mixed in, or right after it), then offices with a national online
 * doctor after every two. Only doctors Saman confirms bookable are kept; one card per doctor.
 */
export type PlanTier = 'preferred' | 'local-online' | 'office' | 'national-online';
/** How the suggested doctor is booked: their in-person calendar or their online visit. */
export type PlanMode = 'in-person' | 'online';

/** Online-visit center; its `city_name` is the platform's (تهران), not the doctor's. */
const ONLINE_VISIT_CENTER_ID = '5532';
/** One national online doctor after every this many offices (owner: «لا به لاش»). */
export const OFFICES_BEFORE_EACH_ONLINE = 2;
/** National online candidates checked with Saman (search order = the platform's ranking). */
export const NATIONAL_ONLINE_CHECKED = 8;

type Availability = { booking_available?: boolean; nearest_time_slot?: string | null } | null | undefined;
export type PlanSaman = { in_person_availability?: Availability; online_visit_availability?: Availability } | null | undefined;

export const planSlug = (hit: any) => {
  const raw = hit?.slug || hit?.url;
  const last = typeof raw === 'string' ? raw.split('/').filter(Boolean).pop() ?? '' : '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

const expertiseMatches = (hit: any, include: string[], exclude?: string[]) => {
  const display: string = hit?.display_expertise ?? '';
  return include.some(text => display.includes(text)) && !exclude?.some(text => display.includes(text));
};

const isOtherDoctor = (hit: any, ownSlug?: string) => (!hit?.type || hit.type === 'doctor') && planSlug(hit) !== ownSlug && !!planSlug(hit);

const uniqueBySlug = (hits: any[]) => {
  const seen = new Set<string>();
  return hits.filter(hit => {
    const slug = planSlug(hit);
    if (seen.has(slug)) return false;
    seen.add(slug);
    return true;
  });
};

const livesIn = (hit: any, cityName: string) =>
  (hit?.centers ?? []).some((center: any) => String(center?.id) !== ONLINE_VISIT_CENTER_ID && center?.city_name === cityName);

/**
 * Pre-Saman candidates. `cityResults`: the profile's route (+ plan.extraRoutes); `nationalResults`: the
 * plan's national online route. Preferred doctors keep their group index (`_group`); they skip the
 * neighbourhood/quality filters — patients chose them — but never the bulk (phone-only) ones.
 */
export const planCandidates = (
  cityResults: any[] | undefined,
  nationalResults: any[] | undefined,
  config: LocalPrivateSpecialistsConfig & { plan: SuggestionPlan },
  ownSlug?: string,
) => {
  const { plan } = config;
  const city = uniqueBySlug((cityResults ?? []).filter(hit => isOtherDoctor(hit, ownSlug)));
  // A hit belongs to the first preferred group that names its slug, or — for a group with `centerIds` — whose
  // center it works at (Dr. Asilian's clinic colleagues); center-matched doctors still need the profile's
  // specialty and the quality floor, named ones are exempt (patients chose them).
  const groupOf = (hit: any) =>
    plan.preferred.findIndex(
      group =>
        group.slugs.includes(planSlug(hit)) ||
        (!!group.centerIds?.length &&
          (hit?.centers ?? []).some((center: any) => group.centerIds!.includes(String(center?.id))) &&
          expertiseMatches(hit, config.includeExpertise, config.excludeExpertise) &&
          meetsQualityFloor(hit, config)),
    );

  const preferred = city.filter(hit => groupOf(hit) >= 0 && !hit?.is_bulk).map(hit => ({ ...hit, _group: groupOf(hit) }));
  const isPreferred = (hit: any) => groupOf(hit) >= 0;

  const localOnline = city.filter(
    hit =>
      !isPreferred(hit) &&
      hit?.consult_active_booking === true &&
      expertiseMatches(hit, plan.localOnline.includeExpertise, plan.localOnline.excludeExpertise) &&
      livesIn(hit, config.cityName) &&
      meetsQualityFloor(hit, config),
  );

  const office = rankLocalSpecialists(city, config, ownSlug).filter(hit => !isPreferred(hit) && hasIndexedInPersonTurn(hit));

  const floor = { minSatisfaction: plan.nationalOnline.minSatisfaction, minRates: plan.nationalOnline.minRates } as LocalPrivateSpecialistsConfig;
  const nationalOnline = uniqueBySlug((nationalResults ?? []).filter(hit => isOtherDoctor(hit, ownSlug)))
    .filter(
      hit =>
        !isPreferred(hit) &&
        hit?.consult_active_booking === true &&
        expertiseMatches(hit, plan.nationalOnline.includeExpertise, plan.nationalOnline.excludeExpertise) &&
        !livesIn(hit, config.cityName) &&
        meetsQualityFloor(hit, floor),
    )
    .slice(0, NATIONAL_ONLINE_CHECKED);

  return { preferred, localOnline, office, nationalOnline };
};

export type PlanCandidates = ReturnType<typeof planCandidates>;

const slotMs = (value?: string | null) => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};

/** Calendar day in Iran (UTC+03:30) of a time in ms — turns on the same day count as equally near. */
const tehranDay = (ms: number) => (Number.isFinite(ms) ? new Date(ms + 3.5 * 3600_000).toISOString().slice(0, 10) : '9999-99-99');

const consultMs = (hit: any) => {
  const value = Number(hit?.consult_freeturn);
  return Number.isFinite(value) && value > 0 ? value * 1000 : Number.POSITIVE_INFINITY;
};

const score = (hit: any) => smoothedSatisfaction(hit?.satisfaction, hit?.rates_count) ?? -1;

/**
 * `extra` mixed into `base`: one after every `every` of base, at most `maxMixed` of them; the rest of
 * `extra` after base. b1 b2 e1 b3 b4 e2 b5 … e3 …
 */
export const interleave = <T>(base: T[], extra: T[], every: number, maxMixed: number): T[] => {
  const { mixed, rest } = interleaveWithRest(base, extra, every, maxMixed);
  return [...mixed, ...rest];
};

/** interleave, with the extras that did not fit between base items returned apart. */
const interleaveWithRest = <T>(base: T[], extra: T[], every: number, maxMixed: number) => {
  const mixed: T[] = [];
  let used = 0;
  base.forEach((item, index) => {
    mixed.push(item);
    if ((index + 1) % every === 0 && used < maxMixed && used < extra.length) {
      mixed.push(extra[used]);
      used += 1;
    }
  });
  return { mixed, rest: extra.slice(used) };
};

/**
 * Final list after the Saman check. Each hit is copied with `_tier`, `_mode` (in-person | online) and
 * `_slot` (Saman's nearest in-person slot, for the line above the button).
 * - preferred: bookable in person or online; group order, then in person before online-only, then the
 *   day of the earliest turn, then a bookable online visit as a bonus, then smoothed satisfaction;
 * - local online: online bookable; earliest online day, then satisfaction; at most localOnline.max;
 * - office: bookable in person; rankLocalSpecialists' order;
 * - national online: online bookable; the search's own order (platform ranking); at most nationalOnline.max.
 */
export const assemblePlan = (
  candidates: PlanCandidates,
  samanBySlug: Record<string, PlanSaman>,
  plan: SuggestionPlan,
  /** The profile's own specialty (config.includeExpertise) — used by plan.specialtyFirst. */
  mainExpertise: string[] = [],
) => {
  const status = (hit: any) => samanBySlug[planSlug(hit)];
  const inPerson = (hit: any) => status(hit)?.in_person_availability?.booking_available === true;
  const online = (hit: any) => status(hit)?.online_visit_availability?.booking_available === true;
  const slot = (hit: any) => status(hit)?.in_person_availability?.nearest_time_slot ?? null;

  const preferred = candidates.preferred
    .filter(hit => inPerson(hit) || online(hit))
    .map(hit => ({
      ...hit,
      _tier: 'preferred' as PlanTier,
      _mode: (inPerson(hit) ? 'in-person' : 'online') as PlanMode,
      _slot: inPerson(hit) ? slot(hit) : null,
    }))
    .sort((a, b) => {
      if (a._group !== b._group) return a._group - b._group;
      if (a._mode !== b._mode) return a._mode === 'in-person' ? -1 : 1;
      const dayA = tehranDay(a._mode === 'in-person' ? slotMs(a._slot) : consultMs(a));
      const dayB = tehranDay(b._mode === 'in-person' ? slotMs(b._slot) : consultMs(b));
      if (dayA !== dayB) return dayA < dayB ? -1 : 1;
      return Number(online(b)) - Number(online(a)) || score(b) - score(a);
    });

  const localOnlineAll = candidates.localOnline
    .filter(online)
    .map(hit => ({ ...hit, _tier: 'local-online' as PlanTier, _mode: 'online' as PlanMode, _slot: null }))
    .sort((a, b) => {
      const dayA = tehranDay(consultMs(a));
      const dayB = tehranDay(consultMs(b));
      if (dayA !== dayB) return dayA < dayB ? -1 : 1;
      return score(b) - score(a);
    });
  const localOnline = localOnlineAll.slice(0, plan.localOnline.max);

  // Saman's own nearest slot, not the search index's (it lags): earliest first, then satisfaction.
  const office = candidates.office
    .filter(inPerson)
    .map(hit => ({ ...hit, _tier: 'office' as PlanTier, _mode: 'in-person' as PlanMode, _slot: slot(hit) }))
    .sort((a, b) => slotMs(a._slot) - slotMs(b._slot) || score(b) - score(a));

  const nationalOnline = candidates.nationalOnline
    .filter(online)
    .map(hit => ({ ...hit, _tier: 'national-online' as PlanTier, _mode: 'online' as PlanMode, _slot: null }))
    .slice(0, plan.nationalOnline.max);

  if (plan.specialtyFirst) {
    // Own specialty first: first preferred group → offices (national online mixed in) → own-specialty
    // local online → remaining national online (same specialty) → other preferred groups → other local online.
    const ownSpecialty = (hit: any) => mainExpertise.some(text => (hit?.display_expertise ?? '').includes(text));
    const firstGroup = uniqueByDoctorName(preferred.filter(hit => hit._group === 0));
    const otherGroups = preferred.filter(hit => hit._group !== 0);
    const taken = new Set(firstGroup.map(doctorNameKey));
    const fresh = <T>(hits: T[]) => {
      const kept = uniqueByDoctorName(hits).filter(hit => !taken.has(doctorNameKey(hit)));
      kept.forEach(hit => taken.add(doctorNameKey(hit)));
      return kept;
    };
    const offices = fresh(office);
    const { mixed, rest } = interleaveWithRest(offices, fresh(nationalOnline), OFFICES_BEFORE_EACH_ONLINE, plan.nationalOnline.max);
    const ownOnline = fresh(localOnlineAll.filter(ownSpecialty).slice(0, plan.localOnline.max));
    const others = fresh(otherGroups);
    const otherOnline = fresh(localOnlineAll.filter(hit => !ownSpecialty(hit)).slice(0, plan.localOnline.max));
    return [...firstGroup, ...mixed, ...ownOnline, ...rest, ...others, ...otherOnline];
  }

  // One card per doctor, decided before mixing: a doctor listed in an earlier tier (e.g. a Kashan
  // gynecologist with online visit, also on the office list) would otherwise drop out after mixing and
  // break the «two offices, one online» rhythm.
  const head = uniqueByDoctorName(
    plan.localOnline.mixIntoPreferred
      ? interleave(preferred, uniqueByDoctorName(localOnline), 2, plan.localOnline.max)
      : [...preferred, ...localOnline],
  );
  const taken = new Set(head.map(doctorNameKey));
  const notTaken = <T>(hits: T[]) => uniqueByDoctorName(hits).filter(hit => !taken.has(doctorNameKey(hit)));
  const tail = interleave(notTaken(office), notTaken(nationalOnline), OFFICES_BEFORE_EACH_ONLINE, plan.nationalOnline.max);
  return uniqueByDoctorName([...head, ...tail]);
};

/**
 * Which of the first doctors the profile card shows: `weights[i]` is the share of visitors who get the doctor at
 * position i (`draw` is one random integer in [0, sum(weights) - 1]). With fewer doctors than weights the draw is
 * folded onto the weights that have a doctor, so there is always a valid index.
 */
export const weightedPosition = (weights: number[], available: number, draw: number): number => {
  const usable = weights.slice(0, Math.max(1, available)).map(weight => Math.max(0, weight));
  const total = usable.reduce((sum, weight) => sum + weight, 0);
  if (!total) return 0;
  let rest = Math.abs(Math.trunc(draw)) % total;
  for (let index = 0; index < usable.length; index += 1) {
    if (rest < usable[index]) return index;
    rest -= usable[index];
  }
  return 0;
};

/** Count per tier, for analytics (view event). */
export const tierCounts = (doctors: { _tier?: PlanTier }[]) =>
  doctors.reduce<Record<string, number>>((counts, doctor) => {
    if (doctor._tier) counts[doctor._tier] = (counts[doctor._tier] ?? 0) + 1;
    return counts;
  }, {});
